/**
 * Heartbeat Manager
 *
 * Manages WebSocket heartbeat timing and connection health monitoring.
 *
 * Ported from: heartbeat.rs (177 lines)
 */

/**
 * Constants from Rust implementation
 */
export const HEARTBEAT_MESSAGE = '[]';
export const HEARTBEAT_INTERVAL_MS = 2500;      // Send heartbeat every 2.5s
export const NO_MESSAGE_TIMEOUT_MS = 12500;     // Dead connection if no message in 12.5s (5 × 2.5s)

/**
 * Heartbeat Manager
 *
 * Tracks last incoming/outgoing message times and determines:
 * - When to send heartbeat (every 2.5s of inactivity)
 * - When connection is dead (no message in 12.5s)
 *
 * Rust equivalent:
 * ```rust
 * pub struct HeartbeatManager {
 *   last_incoming: SharedPtr<AsyncRwLock<Instant>>,
 *   last_outgoing: SharedPtr<AsyncRwLock<Instant>>,
 * }
 * ```
 */
export class HeartbeatManager {
  private lastIncoming: number = Date.now();
  private lastOutgoing: number = Date.now();
  private heartbeatInterval: NodeJS.Timeout | null = null;

  /**
   * Update last incoming message timestamp
   *
   * Call this whenever a message is received from server.
   *
   * Rust equivalent:
   * ```rust
   * pub async fn update_incoming(&self) {
   *   let mut last = self.last_incoming.write().await;
   *   *last = now();
   * }
   * ```
   */
  updateIncoming(): void {
    this.lastIncoming = Date.now();
  }

  /**
   * Update last outgoing message timestamp
   *
   * Call this whenever a message is sent to server.
   *
   * Rust equivalent:
   * ```rust
   * pub async fn update_outgoing(&self) {
   *   let mut last = self.last_outgoing.write().await;
   *   *last = now();
   * }
   * ```
   */
  updateOutgoing(): void {
    this.lastOutgoing = Date.now();
  }

  /**
   * Get time since last incoming message
   *
   * @returns Milliseconds since last incoming message
   */
  timeSinceLastIncoming(): number {
    return Date.now() - this.lastIncoming;
  }

  /**
   * Get time since last outgoing message
   *
   * @returns Milliseconds since last outgoing message
   */
  timeSinceLastOutgoing(): number {
    return Date.now() - this.lastOutgoing;
  }

  /**
   * Check if connection is alive (received message recently)
   *
   * Rust equivalent:
   * ```rust
   * pub async fn is_connection_alive(&self) -> bool {
   *   self.time_since_last_incoming().await.as_millis() < NO_MESSAGE_TIMEOUT_MS as u128
   * }
   * ```
   */
  isConnectionAlive(): boolean {
    return this.timeSinceLastIncoming() < NO_MESSAGE_TIMEOUT_MS;
  }

  /**
   * Check if heartbeat should be sent (been idle for HEARTBEAT_INTERVAL_MS)
   *
   * Rust equivalent:
   * ```rust
   * pub async fn should_send_heartbeat(&self) -> bool {
   *   self.time_since_last_outgoing().await.as_millis() > HEARTBEAT_INTERVAL_MS as u128
   * }
   * ```
   */
  shouldSendHeartbeat(): boolean {
    return this.timeSinceLastOutgoing() > HEARTBEAT_INTERVAL_MS;
  }

  /**
   * Start heartbeat loop
   *
   * Checks connection health and sends heartbeats on interval.
   * Automatically stops if connection dies or sendFn returns false.
   *
   * @param sendFn - Function to send heartbeat (should send HEARTBEAT_MESSAGE)
   * @returns Cleanup function to stop heartbeat loop
   *
   * Rust equivalent:
   * ```rust
   * pub fn spawn_heartbeat_task<F>(
   *   self: SharedPtr<Self>,
   *   mut send_heartbeat: F,
   * ) -> Task<()>
   * where F: FnMut() -> bool + Send + 'static {
   *   spawn(async move {
   *     let mut ticker = create_interval(Duration::from_millis(HEARTBEAT_INTERVAL_MS / 2));
   *     loop {
   *       tick(&mut ticker).await;
   *       if !self.is_connection_alive().await { break; }
   *       if self.should_send_heartbeat().await {
   *         if !send_heartbeat() { break; }
   *         self.update_outgoing().await;
   *       }
   *     }
   *   })
   * }
   * ```
   */
  startHeartbeatLoop(sendFn: () => boolean): () => void {
    // Check twice as often as heartbeat interval (like Rust ticker)
    const checkInterval = HEARTBEAT_INTERVAL_MS / 2;

    this.heartbeatInterval = setInterval(() => {
      // Check if connection is alive
      if (!this.isConnectionAlive()) {
        console.warn('💔 Connection appears dead (no message in 12.5s)');
        this.stopHeartbeatLoop();
        return;
      }

      // Send heartbeat if needed
      if (this.shouldSendHeartbeat()) {
        const sent = sendFn();

        if (!sent) {
          // Send failed, stop heartbeat
          this.stopHeartbeatLoop();
          return;
        }

        this.updateOutgoing();
        console.debug('💓 Heartbeat sent');
      }
    }, checkInterval);

    console.log('💓 Heartbeat loop started (interval: 2.5s, check: 1.25s)');

    // Return cleanup function
    return () => this.stopHeartbeatLoop();
  }

  /**
   * Stop heartbeat loop
   */
  stopHeartbeatLoop(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      console.log('💔 Heartbeat loop stopped');
    }
  }

  /**
   * Reset timestamps (call when reconnecting)
   */
  reset(): void {
    this.lastIncoming = Date.now();
    this.lastOutgoing = Date.now();
  }

  /**
   * Get status for debugging
   */
  getStatus(): {
    lastIncoming: number;
    lastOutgoing: number;
    timeSinceIncoming: number;
    timeSinceOutgoing: number;
    isAlive: boolean;
    shouldSendHeartbeat: boolean;
  } {
    return {
      lastIncoming: this.lastIncoming,
      lastOutgoing: this.lastOutgoing,
      timeSinceIncoming: this.timeSinceLastIncoming(),
      timeSinceOutgoing: this.timeSinceLastOutgoing(),
      isAlive: this.isConnectionAlive(),
      shouldSendHeartbeat: this.shouldSendHeartbeat()
    };
  }
}
